'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import BaseAuctionCard from './BaseAuctionCard';
import DepositPaymentPopup from '../DepositPaymentPopup';

interface DepositPendingCardProps {
  id: string;
  slug: string;
  title: string;
  location: string;
  winningBid: number;
  depositAmount: number;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
  };
  image: string;
  depositCode: string;
  districtName: string;
  cityName: string;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isAuthenticated?: boolean; // Server-side'dan gelecek
}

export default function DepositPendingCard({
  id,
  slug,
  title,
  location,
  winningBid,
  depositAmount,
  timeLeft,
  image,
  depositCode,
  districtName,
  cityName,
  isFavorite = false,
  onFavoriteToggle,
  isAuthenticated = false
}: DepositPendingCardProps) {
  const t = useTranslations('profile');
  const params = useParams();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };


  const favoriteButton = isAuthenticated && onFavoriteToggle ? (
    <button
      onClick={() => onFavoriteToggle(id)}
      className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
    >
      <svg 
        className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
        fill={isFavorite ? 'currentColor' : 'none'} 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    </button>
  ) : null;

  const imageOverlay = (
    <div className="absolute top-4 left-4 right-4">
      <div className="bg-yellow-500 text-white text-sm font-semibold px-3 py-2 rounded-full text-center">
        {params.locale === 'tr' 
          ? `ÖDEME İÇİN SON: ${timeLeft.days} GÜN ${timeLeft.hours} SA`
          : `LAST FOR PAYMENT: ${timeLeft.days} DAYS ${timeLeft.hours} HRS`
        }
      </div>
    </div>
  );

  const content = (
    <div className="space-y-3 mb-4">
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('deposits.winningBid')}</span>
        <span className="text-2xl font-bold text-green-600">
          {formatPrice(winningBid)}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('deposits.depositAmount')}</span>
        <span className="text-2xl font-bold text-red-600">
          {formatPrice(depositAmount)}
        </span>
      </div>
    </div>
  );

  const actionButton = (
    <div className="mt-auto">
      <Link href={`/${params.locale}/muzayedeler/${slug}`} className="block mb-3">
        <button className="w-full py-3 px-4 rounded-lg font-bold text-red-600 bg-white border-2 border-red-600 hover:bg-red-50 transition-colors">
          {t('deposits.viewDetails')}
        </button>
      </Link>
      <button 
        onClick={() => setShowPaymentPopup(true)}
        className="w-full py-3 px-4 rounded-lg font-bold text-white bg-red-600 hover:bg-red-700 transition-colors"
      >
        {t('deposits.payDeposit')}
      </button>
    </div>
  );

  return (
    <>
      <BaseAuctionCard
        id={id}
        title={title}
        location={location}
        image={image}
        imageOverlay={imageOverlay}
        favoriteButton={favoriteButton}
        actionButton={actionButton}
      >
        {content}
      </BaseAuctionCard>

      {/* Deposit Payment Popup */}
      <DepositPaymentPopup
        isOpen={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
        auctionTitle={title}
        depositAmount={depositAmount}
        auctionId={id}
        image={image}
        depositCode={depositCode}
        districtName={districtName}
        cityName={cityName}
      />
    </>
  );
}
